import { ApiResponseSimple, ErrorCallbackFunction, deleteVoid, getJson, getObject, getText, legacy, postJson, postObject, putVoid } from "../ChoiceApi";
import { IUserInfo, IUserTimeZoneRequest, IUserTimeZoneResponse, IUserUpdatePasswordInputs, IUserUpdatePasswordResponse } from "../Models/UserInfoModel";
import { SimpleUserInfoModel } from "../Models/User/SimpleUserInfoModel";
import { UserPinModel } from "../Models/User/UserValidation";
import { PrivilegeType, UserSettingGroup, AppScreenNames, TwoFactorTokenMode, TwoFactorApplicationTypes, TwoFactorEnforcementType, TwoFactorTokenTypes, DatabaseIdTypes } from '../../Enums/ChoiceEnums';
import { create as createXml } from "xmlbuilder2";
import { IResult } from '../Models/Result';
import { IDatabaseIdModel } from '../Models/DatabaseIdModel';
import { IAuthenicatorSetupRespModel } from "./Access";
import { ApiResponse } from "./Common";

export async function AllUsers(): Promise<IUserInfo[]> {
    return (await getJson("api/User/Users/All")) as IUserInfo[];
}

export async function SimpleInfo(user_id: string): Promise<SimpleUserInfoModel> {
    return (await getJson(`api/User/Simple/${user_id}`)) as SimpleUserInfoModel;
}

export async function SimpleInfoByIdType(idtype: DatabaseIdTypes,id: string): Promise<SimpleUserInfoModel> {
    return (await getJson(`api/User/Simple/${idtype}/${id}`)) as SimpleUserInfoModel;
}

export async function GetUserGroupInfo(id: number): Promise<UserGroupInfoResult> {
    return (await getJson(`api/User/GetUserGroup/${id}`)) as UserGroupInfoResult;
}

export async function ValidateUserPin(userPin: number): Promise<UserPinModel> {
    return (await getJson(`api/User/ValidatePin/${userPin}`)) as UserPinModel;
}

// Get the users max rights for the specified screen
export async function screenPrivilege(screenName: string): Promise<PrivilegeType> {
    const retVal = (await getText(`api/User/Privilege/Screen/${screenName}`));
    if (!retVal)
        return PrivilegeType.None;
    return (+retVal) as PrivilegeType;
}

// Get the max user rights for the specified screens
export async function getMultipleScreenPrivileges(screenName: string[]): Promise<{ screenName: string, privilege: PrivilegeType }[]> {
    return (await postJson("api/User/Privilege/Screens", screenName));
}

export async function useScreen(screenName: AppScreenNames|string): Promise<UseScreenResp | undefined> {
    return (await getObject<UseScreenResp>(`api/User/Screen/Use/${screenName}`));
}

export async function getSetting(groupName: UserSettingGroup, codeName: string): Promise<string | undefined> {
    return (await getText(`api/User/Setting/${groupName}/${codeName}`));
}

export async function getSettingGroupValues(groupName: UserSettingGroup): Promise<IGroupSettingValue[] | undefined> {
    return (await getObject(`api/User/Setting/Group/${groupName}`));
}

export async function saveSetting(group: UserSettingGroup, codeName: string, value?: unknown, errorCallback?: ErrorCallbackFunction): Promise<boolean> {
    const errMsg = await putVoid("api/User/Setting", {group: group, codeName: codeName, setting: value}, { errorCallback });
    if (!errMsg)
        return true;
    return false;
}

// Get the 2FA info for a specified user
export async function getLegacyTwoFactorInfo(securityUserId?: number|string, userId?: string) : Promise<UserTwoFactorInfo|undefined>
{
    const xmlDoc = createXml();
    const screenInput = xmlDoc.ele("screen_input");
    if (securityUserId)
        screenInput.ele("security_users_id").txt(securityUserId?.toString() ?? "-1");
    if (userId)
        screenInput.ele("users_id").txt(userId);

    const resp = await legacy.initialize(AppScreenNames.User2FA, "initialize", xmlDoc.end({headless: true}));

    if (!resp)
        return;

    return {
        userId: resp.querySelector("initialize > input_users_id")?.innerHTML,
        twoFactorMode: (resp.querySelector("initialize > token_type")?.innerHTML as TwoFactorTokenMode),
        tokenContact: resp.querySelector("initialize > token_contact")?.innerHTML
    }
}

// send a 2FA token to the specified user
export async function sendTwoFactorToken(userId: string, twoFactorMode: TwoFactorTokenMode, tokenContact: string, appErrorCallback?:(errorMsg?: string) => void): Promise<SendTokenResp|undefined>
{
    const xmlDoc = createXml();
    const screenInput = xmlDoc.ele("screen_input");
    screenInput.ele("input_users_id").txt(userId);
    screenInput.ele("token_type").txt(twoFactorMode);
    screenInput.ele("token_contact").txt(tokenContact);

    const resp = await legacy.initialize(AppScreenNames.User2FA, "send", xmlDoc.end({headless: true}));
    if (!resp)
        return;

    // Handle any errors
    if (legacy.soapErrorMsg(resp, appErrorCallback))
        return;

    return {
        token: resp.querySelector("send > token")?.innerHTML
    }
}

export async function saveLegacyTwoFactorSettings(userId: string, twoFactorMode: TwoFactorTokenMode, tokenContact?: string, appErrorCallback?:(errorMsg?: string) => void): Promise<SaveTwoFactorSettingsResp | undefined>
{
    const xmlDoc = createXml();
    const screenInput = xmlDoc.ele("screen_input");
    screenInput.ele("input_users_id").txt(userId);
    screenInput.ele("token_type").txt(twoFactorMode);
    if (tokenContact)
        screenInput.ele("token_contact").txt(tokenContact);

    const resp = await legacy.initialize(AppScreenNames.User2FA, "save", xmlDoc.end({headless: true}));
    if (!resp)
        return;

    // Handle any errors
    if (legacy.soapErrorMsg(resp, appErrorCallback))
        return;
    
    return { tokenSent: ("1" === resp.querySelector("token_sent")?.innerHTML) };
}

// Maybe merge with getTwoFactorInfo??
export async function getTwoFactorAppInfo(userDatabaseId: IDatabaseIdModel): Promise<TwoFactorAppInfoResp|undefined> {
    return await getObject<TwoFactorAppInfoResp>(`api/User/TwoFactorAppInfo/${userDatabaseId.idType.toString()}/${userDatabaseId.id}`);
}

export async function saveTwoFactorAppInfo(user_id: string, twoFactorEnforcement: TwoFactorEnforcementType, twoFactorAppType: TwoFactorApplicationTypes = TwoFactorApplicationTypes.OneTimePassword_Google): Promise<IResult|undefined> {
    return await postObject<IResult>("api/User/TwoFactorAppInfo", { user_id, twoFactorEnforcement, twoFactorAppType });
}

export async function getUserTimeZoneInfo(payload: IUserTimeZoneRequest): Promise<IUserTimeZoneResponse|undefined> {
    return await postObject<IUserTimeZoneResponse>("api/User/UserTimeZone/GetUserTimeZoneInfo", payload);   
}

export async function getAuthenticatorSetup(encKey: string, appEntryText: string, userId: string): Promise<IAuthenicatorSetupRespModel | undefined> {
    return await postObject<IAuthenicatorSetupRespModel>(`api/User/AuthenticatorSetup/${userId}`, {encKey, appEntryText});
}

export async function logOut(message?: LogOutMessage, errorCallback?: ErrorCallbackFunction): Promise<void>{
    switch (message)
    {
        case undefined:
        case LogOutMessage.GuidValidationFailed:
        case LogOutMessage.UserSessionExpired:
        break;
        
        default:
        alert("Unexpected message passed for logout");
    }

    await deleteVoid(`api/User/Session/Logout`, {message: message}, errorCallback);
}

// Get the JWT used to keep the cached login key up to date
//  'success' is false if we cannot authenicate the user session (ie. HTTP 401 returned) 
//  'success' is true in all other cases. 'data' is only a valid JWT if the call was fully successful
export async function getSessionPulseToken(): Promise<ApiResponseSimple<string>|undefined>{
    let isSuccess = true;
    const respData = await getObject<ApiResponseSimple<string>>("api/User/Session/PulseToken", err => { 
        // If we have an auth rejection, treat the call as a failure
        if (401 === err.status)
        {
            isSuccess = false;
            return;
        }
        console.warn(`getSessionPulseToken() call not successful: ${err.status}`);
    });
    if (!isSuccess)
        return { success: false };
    return respData;
}

// Get the ProductRoadmap SSO Token
export async function getProductRoadmapSSOToken(): Promise<string|undefined>{
    return await getText("api/User/ProductBoard/Token", err => {});
}

export async function UpdatePassword(payload: IUserUpdatePasswordInputs): Promise<IUserUpdatePasswordResponse|undefined> {
    return await postObject<IUserUpdatePasswordResponse>("api/User/UpdatePassword", payload);   
}

export interface UseScreenResp { screenName: string, use: boolean };
export interface UserTwoFactorInfo { userId?: string, twoFactorMode?: TwoFactorTokenMode, tokenContact?: string }
export interface SendTokenResp { token?: string }
export interface SaveTwoFactorSettingsResp { tokenSent: boolean }
export interface TwoFactorAppInfoResp { proposedEncryptedTwoFactorKey: string, twoFactorAppType?: TwoFactorApplicationTypes, twoFactorEnforcement?: TwoFactorEnforcementType, twoFactorToken?: TwoFactorTokenTypes, twoFactorAppEnforcementTypeSetting?: TwoFactorEnforcementType, suggestedAppEntryText: string }
export interface SaveTwoFactorSetupReq { securityUserId: number, twoFactorEnforcement?: TwoFactorEnforcementType, twoFactorAppType?: TwoFactorApplicationTypes };
export interface UserGroupInfoResult { userGroupId: number, description?: string };
export interface IGroupSettingValue { groupName: string, parmCodeName: string, value?: string }
export enum LogOutMessage
{
    GuidValidationFailed = "GUID Validation failed",
    UserSessionExpired = "User session expired"
}
